import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryFunctionContext,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { RFSData } from "./rfs";
import { Paginated } from "./types";

interface HPTARData {
  id: string;
  documentHptarName: string;
  documentHptarPath: string;
  valid: boolean;
  revise: boolean;
  lastUpdate: string | null;
  updatedDate: string;
  lastUpdateCableSlingwireQspan: string | null;
  hasTechnicalChange: boolean | null;
}

/* ========== *
 * READ HPTAR *
 * ========== */

interface HPTARQueryKey {
  rfsId: string;
}

async function getHPTARs(
  queryFunctionContext: QueryFunctionContext,
): Promise<HPTARData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, hptarQueryKey] = queryKey;
  const { rfsId } = hptarQueryKey as HPTARQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/hptar" + `?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data.content;
}
function useHPTARs(hptarQueryKey: HPTARQueryKey) {
  const { rfsId } = hptarQueryKey;
  return useQuery({
    queryKey: ["hptars", { rfsId }],
    queryFn: getHPTARs,
  });
}

interface InfiniteHPTARs extends Paginated {
  content: HPTARData[];
}

async function getInfiniteHPTARs(
  queryFunctionContext: QueryFunctionContext,
): Promise<InfiniteHPTARs> {
  const { queryKey, pageParam } = queryFunctionContext;
  const [_, hptarQueryKey] = queryKey;
  const { rfsId } = hptarQueryKey as HPTARQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/hptar" +
      `?rfs.id=${rfsId ?? ""}&sort=updatedDate&asc=false&page=${pageParam}`,
  );
  return response.data.data;
}
function useInfiniteHPTARs(hptarQueryKey: HPTARQueryKey) {
  const { rfsId } = hptarQueryKey;
  return useInfiniteQuery({
    queryKey: ["infinite-hptars", { rfsId }],
    queryFn: getInfiniteHPTARs,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage.number + 1 < lastPage.totalPages
        ? lastPage.number + 1
        : undefined;
    },
  });
}

/* ====================== *
 * READ HPTAR INVALID *
 * ====================== */

interface HPTARDetailData {
  message?: string;
  warnColor?: string;
  validation?: string;
}

interface HPTARValueData {
  value: string;
  details: HPTARDetailData[];
}

interface HPTARInvalidData {
  id: string;
  message: HPTARDetailData[];
  folder: HPTARValueData;
  intervalId: HPTARValueData;
  clusterCode: HPTARValueData;
  siteId: HPTARValueData;
  locality: HPTARValueData;
  prefixAddress: HPTARValueData;
  stName: HPTARValueData;
  stNo: HPTARValueData;
  block: HPTARValueData;
  floor: HPTARValueData;
  rt: HPTARValueData;
  rw: HPTARValueData;
  postCode: HPTARValueData;
  subDistrict: HPTARValueData;
  village: HPTARValueData;
  fiberNode: HPTARValueData;
  fdtType: HPTARValueData;
  latitude: HPTARValueData;
  longitude: HPTARValueData;
  categoryBizzpass: HPTARValueData;
  addressPole: HPTARValueData;
  ovUg: HPTARValueData;
  houseComment: HPTARValueData;
  buildingName: HPTARValueData;
  tower: HPTARValueData;
  aptn: HPTARValueData;
  fiberNodeHfc: HPTARValueData;
  idArea: HPTARValueData;
  fdtCode: HPTARValueData;
  fatCode: HPTARValueData;
  poleId: HPTARValueData;
  poleLat: HPTARValueData;
  poleLong: HPTARValueData;
  poleProvider: HPTARValueData;
  poleType: HPTARValueData;
  poleLine: HPTARValueData;
  clampHookId: HPTARValueData;
  clampHookLatitude: HPTARValueData;
  clampHookLongitude: HPTARValueData;
  deploymentType: HPTARValueData;
  needSurvey: HPTARValueData;
  statusHomepass: HPTARValueData;
  reason: HPTARValueData;
  rfs: RFSData;
}

async function getHPTARInvalids(
  queryFunctionContext: QueryFunctionContext,
): Promise<HPTARInvalidData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, hptarQueryKey] = queryKey;
  const { rfsId } = hptarQueryKey as HPTARQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/hptar-invalid" + `?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data.content;
}
function useHPTARInvalids(hptarQueryKey: HPTARQueryKey) {
  const { rfsId } = hptarQueryKey;
  return useQuery({
    queryKey: ["hptar-invalids", { rfsId }],
    queryFn: getHPTARInvalids,
  });
}

async function getHPTARGeneralMessage(
  queryFunctionContext: QueryFunctionContext,
): Promise<{ id: string; message: string }[]> {
  const { queryKey } = queryFunctionContext;
  const [_, hptarQueryKey] = queryKey;
  const { rfsId } = hptarQueryKey as HPTARQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/hptar-invalid-gm" + `?rfs.id=${rfsId ?? ""}`,
  );
  return response.data.data.content;
}
function useHPTARGeneralMessage(hptarQueryKey: HPTARQueryKey) {
  const { rfsId } = hptarQueryKey;
  return useQuery({
    queryKey: ["hptar-invalid-gm", { rfsId }],
    queryFn: getHPTARGeneralMessage,
  });
}

interface InfiniteHPTARInvalids extends Paginated {
  content: HPTARInvalidData[];
}

async function getInfiniteHPTARInvalids(
  queryFunctionContext: QueryFunctionContext,
): Promise<InfiniteHPTARInvalids> {
  const { queryKey, pageParam } = queryFunctionContext;
  const [_, hptarQueryKey] = queryKey;
  const { rfsId } = hptarQueryKey as HPTARQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/hptar-invalid" + `?rfs.id=${rfsId ?? ""}&page=${pageParam}`,
  );
  return response.data.data;
}
function useInfiniteHPTARInvalids(hptarQueryKey: HPTARQueryKey) {
  const { rfsId } = hptarQueryKey;
  return useInfiniteQuery({
    queryKey: ["infinite-hptar-invalids", { rfsId }],
    queryFn: getInfiniteHPTARInvalids,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage.number + 1 < lastPage.totalPages
        ? lastPage.number + 1
        : undefined;
    },
  });
}

interface KmzInvalidMessagesData {
  key: string;
  field: string;
  message: string;
  value: string;
  latitude: string | null;
  longitude: string | null;
}

interface InfiniteKmzInvalids extends Paginated {
  content: {
    key: string;
    message: KmzInvalidMessagesData[];
  }[];
}

async function getInfiniteKmzInvalids(
  queryFunctionContext: QueryFunctionContext,
): Promise<InfiniteKmzInvalids> {
  const { queryKey, pageParam } = queryFunctionContext;
  const [_, hptarQueryKey] = queryKey;
  const { rfsId } = hptarQueryKey as HPTARQueryKey;

  const response = await axiosInstance.get(
    "/api/crud/hptar-kmz-invalid" + `?rfs.id=${rfsId ?? ""}&page=${pageParam}`,
  );
  return response.data.data;
}
function useInfiniteKmzInvalids(hptarQueryKey: HPTARQueryKey) {
  const { rfsId } = hptarQueryKey;
  return useInfiniteQuery({
    queryKey: ["infinite-kmz-invalids", { rfsId }],
    queryFn: getInfiniteKmzInvalids,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage.number + 1 < lastPage.totalPages
        ? lastPage.number + 1
        : undefined;
    },
  });
}

/* ====================== *
 * GENERATE HPTAR INVALID *
 * ====================== */

async function generateHPTARInvalidXlsx(id: string, name?: string) {
  const response = await axiosInstance.post(
    `/api/crud/hptar-invalid/excel?rfs.id=${id}`,
    null,
    {
      responseType: "blob",
    },
  );

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;

  const contentDisposition = response.request.getResponseHeader(
    "content-disposition",
  );
  const filename =
    name ??
    (contentDisposition
      ? contentDisposition.split("filename=")[1]
      : `${id}.xlsx`);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
function useGenerateHPTARInvalidXlsx() {
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) =>
      generateHPTARInvalidXlsx(id, name),
  });
}

async function postTechnicalChange(id: string, hasTechnicalChange: boolean) {
  const response = await axiosInstance.post(
    `/api/crud/hptar/${id}/technical-change`,
    { hasTechnicalChange },
  );
  return response.data;
}
function useTechnicalChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      hasTechnicalChange,
    }: {
      id: string;
      hasTechnicalChange: boolean;
    }) => postTechnicalChange(id, hasTechnicalChange),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hptars"] });
      queryClient.invalidateQueries({ queryKey: ["historyUpdateHomepass"] });
      queryClient.invalidateQueries({ queryKey: ["rfss"] });
    },
  });
}

export type {
  HPTARData,
  HPTARDetailData,
  HPTARInvalidData,
  InfiniteKmzInvalids,
  KmzInvalidMessagesData,
};
export {
  useHPTARs,
  useHPTARInvalids,
  useGenerateHPTARInvalidXlsx,
  useInfiniteHPTARInvalids,
  useInfiniteHPTARs,
  useHPTARGeneralMessage,
  useTechnicalChange,
  useInfiniteKmzInvalids,
};
